import type {
  OneFSAdapter,
  OneFSFile,
  OneFSOpenOptions,
  OneFSSaveOptions,
  OneFSDirectory,
  OneFSDirectoryOptions,
  OneFSReadDirectoryOptions,
  OneFSScanOptions,
  OneFSEntry,
  StoredHandle,
  OneFSResult,
} from '../types'
import { ok, err } from '../types'
import { IDBStorage } from '../storage/idb'
import { generateId, getMimeType, getFileName, isPathWithin, normalizePath, toArrayBuffer, isSafeEntryName } from '../utils'

type TauriDialog = typeof import('@tauri-apps/plugin-dialog')
type TauriFS = typeof import('@tauri-apps/plugin-fs')
type TauriCore = typeof import('@tauri-apps/api/core')

const DIRECTORY_MIME_TYPE = 'inode/directory'
const STAT_CHUNK_SIZE = 25

export class TauriAdapter implements OneFSAdapter {
  platform = 'tauri' as const
  private storage: IDBStorage
  private dialog: TauriDialog | null = null
  private fs: TauriFS | null = null
  private core: TauriCore | null = null
  private persistByDefault: boolean
  private scanLock: Promise<void> = Promise.resolve()
  private sessionPaths = new Map<string, string>()

  constructor(appName: string, maxRecentFiles = 10, persistByDefault = true) {
    this.storage = new IDBStorage(appName, maxRecentFiles)
    this.persistByDefault = persistByDefault
  }

  isSupported(): boolean {
    if (typeof window === 'undefined') return false
    return '__TAURI_INTERNALS__' in window
  }

  private async loadModules(): Promise<{ dialog: TauriDialog; fs: TauriFS; core: TauriCore }> {
    if (!this.dialog || !this.fs || !this.core) {
      const [dialog, fs, core] = await Promise.all([
        import('@tauri-apps/plugin-dialog'),
        import('@tauri-apps/plugin-fs'),
        import('@tauri-apps/api/core'),
      ])
      this.dialog = dialog
      this.fs = fs
      this.core = core
    }
    return { dialog: this.dialog, fs: this.fs, core: this.core }
  }

  private async acquireScanLock(): Promise<() => void> {
    let release: () => void
    const next = new Promise<void>(resolve => { release = resolve })
    const prev = this.scanLock
    this.scanLock = next
    await prev
    return release!
  }

  private getPreferredSeparator(path: string): '/' | '\\' {
    return path.includes('\\') && !path.includes('/') ? '\\' : '/'
  }

  private joinPath(parent: string, child: string): string {
    if (!parent) return child
    const separator = this.getPreferredSeparator(parent)
    const normalizedParent = parent.endsWith('/') || parent.endsWith('\\') ? parent.slice(0, -1) : parent
    return `${normalizedParent}${separator}${child}`
  }

  private splitParentPath(path: string): { parent: string; separator: '/' | '\\' } | null {
    const forwardSlash = path.lastIndexOf('/')
    const backwardSlash = path.lastIndexOf('\\')
    const index = Math.max(forwardSlash, backwardSlash)
    if (index < 0) return null

    const parent = path.slice(0, index)
    return {
      parent,
      separator: this.getPreferredSeparator(parent),
    }
  }

  private registerSessionFile(file: Pick<OneFSFile, 'id' | 'path'>): void {
    if (!file.path) return
    this.sessionPaths.set(file.id, file.path)
  }

  private pathsMatch(left: string, right: string): boolean {
    return normalizePath(left) === normalizePath(right)
  }

  private async resolveAuthorizedPath(file: OneFSFile): Promise<OneFSResult<string>> {
    if (!file.path) {
      return err('not_supported', 'Cannot operate on file without path')
    }

    const sessionPath = this.sessionPaths.get(file.id)
    if (sessionPath && this.pathsMatch(sessionPath, file.path)) {
      return ok(file.path)
    }

    try {
      const stored = await this.storage.getStoredFile(file.id)
      if (stored?.path && this.pathsMatch(stored.path, file.path)) {
        return ok(stored.path)
      }
      return err('permission_denied', 'File was not opened through this adapter')
    } catch (e) {
      return err('io_error', 'Failed to verify file provenance', e)
    }
  }

  async openFile(options: OneFSOpenOptions = {}): Promise<OneFSResult<OneFSFile | OneFSFile[]>> {
    const shouldPersist = options.persist ?? this.persistByDefault

    try {
      const { dialog, fs } = await this.loadModules()

      const filters =
        options.accept?.length
          ? [{ name: 'Accepted files', extensions: options.accept.map((a) => a.replace('.', '')) }]
          : undefined

      const result = await dialog.open({
        multiple: options.multiple ?? false,
        filters,
      })

      if (!result) {
        return err('cancelled', 'User cancelled file picker')
      }

      const paths = Array.isArray(result) ? result : [result]

      const fileDataResults = await Promise.all(
        paths.map(async (path) => {
          const content = await fs.readFile(path)
          return { path, content }
        })
      )

      const files: OneFSFile[] = fileDataResults.map(({ path, content }) => {
        const name = getFileName(path)
        const id = generateId()

        if (shouldPersist) {
          this.storage.storeFileDeferred({
            id,
            name,
            path,
            content,
            mimeType: getMimeType(name),
            size: content.byteLength,
            lastModified: Date.now(),
            storedAt: Date.now(),
          })
        }

        const file: OneFSFile = {
          id,
          name,
          path,
          content,
          mimeType: getMimeType(name),
          size: content.byteLength,
          lastModified: Date.now(),
        }
        this.registerSessionFile(file)
        return file
      })

      return ok(options.multiple ? files : files[0])
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'File not found', e)
      }
      return err('io_error', error.message || 'Failed to open file', e)
    }
  }

  async saveFile(
    file: OneFSFile,
    content: Uint8Array | string,
    options?: OneFSSaveOptions
  ): Promise<OneFSResult<boolean>> {
    const shouldPersist = options?.persist ?? this.persistByDefault
    const authorized = await this.resolveAuthorizedPath(file)
    if (!authorized.ok) return authorized
    const path = authorized.data

    try {
      const { fs } = await this.loadModules()
      const contentArray = typeof content === 'string' ? new TextEncoder().encode(content) : content

      await fs.writeFile(path, contentArray)
      this.registerSessionFile({ id: file.id, path })

      if (shouldPersist) {
        this.storage.storeFileDeferred({
          id: file.id,
          name: file.name,
          path,
          content: contentArray,
          mimeType: file.mimeType,
          size: contentArray.byteLength,
          lastModified: Date.now(),
          storedAt: Date.now(),
        })
      }

      return ok(true)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to save file', e)
      }
      return err('io_error', error.message || 'Failed to save file', e)
    }
  }

  async saveFileAs(content: Uint8Array | string, options: OneFSSaveOptions = {}): Promise<OneFSResult<OneFSFile>> {
    const shouldPersist = options.persist ?? this.persistByDefault

    try {
      const { dialog, fs } = await this.loadModules()

      const filters =
        options.accept?.length
          ? [{ name: 'Accepted files', extensions: options.accept.map((a) => a.replace('.', '')) }]
          : undefined

      const path = await dialog.save({
        defaultPath: options.suggestedName,
        filters,
      })

      if (!path) {
        return err('cancelled', 'User cancelled save dialog')
      }

      const contentArray = typeof content === 'string' ? new TextEncoder().encode(content) : content
      await fs.writeFile(path, contentArray)

      const name = getFileName(path)
      const id = generateId()

      if (shouldPersist) {
        this.storage.storeFileDeferred({
          id,
          name,
          path,
          content: contentArray,
          mimeType: getMimeType(name),
          size: contentArray.byteLength,
          lastModified: Date.now(),
          storedAt: Date.now(),
        })
      }

      const file: OneFSFile = {
        id,
        name,
        path,
        content: contentArray,
        mimeType: getMimeType(name),
        size: contentArray.byteLength,
        lastModified: Date.now(),
      }
      this.registerSessionFile(file)
      return ok(file)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to save file', e)
      }
      return err('io_error', error.message || 'Failed to save file', e)
    }
  }

  async openDirectory(options: OneFSDirectoryOptions = {}): Promise<OneFSResult<OneFSDirectory>> {
    const shouldPersist = options.persist ?? this.persistByDefault

    try {
      const { dialog } = await this.loadModules()

      const path = await dialog.open({
        directory: true,
      })

      if (!path || Array.isArray(path)) {
        return err('cancelled', 'User cancelled directory picker')
      }

      const id = generateId()

      if (shouldPersist) {
        this.storage.storeFileDeferred({
          id,
          name: getFileName(path),
          path,
          content: new Uint8Array(0),
          mimeType: DIRECTORY_MIME_TYPE,
          size: 0,
          lastModified: Date.now(),
          storedAt: Date.now(),
        })
      }

      return ok({
        id,
        name: getFileName(path),
        path,
      })
    } catch (e) {
      const error = e as Error
      return err('io_error', error.message || 'Failed to open directory', e)
    }
  }

  async readDirectory(
    directory: OneFSDirectory,
    options: OneFSReadDirectoryOptions = {}
  ): Promise<OneFSResult<OneFSEntry[]>> {
    if (!directory.path) {
      return err('not_supported', 'Cannot read directory without path')
    }

    try {
      const { fs } = await this.loadModules()
      const dirEntries = await fs.readDir(directory.path)
      const entries: OneFSEntry[] = []
      const needsStat: { index: number; name: string; path: string }[] = []

      for (const entry of dirEntries) {
        if (!entry.name) continue

        if (!isSafeEntryName(entry.name)) continue
        const entryName = entry.name
        const filePath = this.joinPath(directory.path, entryName)

        if (!isPathWithin(filePath, directory.path)) continue

        if (entry.isFile) {
          if (options.skipStats) {
            entries.push({ name: entryName, kind: 'file', path: filePath })
          } else {
            needsStat.push({ index: entries.length, name: entryName, path: filePath })
            entries.push({ name: entryName, kind: 'file', path: filePath })
          }
        } else if (entry.isDirectory) {
          entries.push({ name: entryName, kind: 'directory', path: filePath })
        }
      }

      for (let i = 0; i < needsStat.length; i += STAT_CHUNK_SIZE) {
        const chunk = needsStat.slice(i, i + STAT_CHUNK_SIZE)
        const stats = await Promise.all(
          chunk.map(entry =>
            fs.stat(entry.path).catch(e => {
              options.onError?.(entry.path, e)
              return null
            })
          )
        )
        for (let j = 0; j < chunk.length; j++) {
          const stat = stats[j]
          if (stat) {
            entries[chunk[j].index] = {
              name: chunk[j].name,
              kind: 'file',
              size: stat.size,
              lastModified: stat.mtime ? new Date(stat.mtime).getTime() : Date.now(),
              path: chunk[j].path,
            }
          }
        }
      }

      return ok(entries)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'Directory not found', e)
      }
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to read directory', e)
      }
      return err('io_error', error.message || 'Failed to read directory', e)
    }
  }

  async readFileFromDirectory(
    directory: OneFSDirectory,
    entry: OneFSEntry,
    _options?: { maxBytes?: number }
  ): Promise<OneFSResult<OneFSFile>> {
    if (!entry.path || entry.kind !== 'file') {
      return err('not_supported', 'Cannot read file without path')
    }

    if (directory.path && !isPathWithin(entry.path, directory.path)) {
      return err('permission_denied', 'Path is outside the expected directory')
    }

    try {
      const { fs } = await this.loadModules()
      const content = await fs.readFile(entry.path)

      const loadedFile: OneFSFile = {
        id: generateId(),
        name: entry.name,
        path: entry.path,
        content,
        mimeType: getMimeType(entry.name),
        size: content.byteLength,
        lastModified: entry.lastModified ?? Date.now(),
      }
      this.registerSessionFile(loadedFile)
      return ok(loadedFile)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'File not found', e)
      }
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to read file', e)
      }
      return err('io_error', error.message || 'Failed to read file', e)
    }
  }

  async scanDirectory(
    directory: OneFSDirectory,
    options: OneFSScanOptions = {}
  ): Promise<OneFSResult<OneFSEntry[]>> {
    const release = await this.acquireScanLock()
    try {
      return await this._scanDirectoryImpl(directory, options)
    } finally {
      release()
    }
  }

  private async _scanDirectoryImpl(
    directory: OneFSDirectory,
    options: OneFSScanOptions = {}
  ): Promise<OneFSResult<OneFSEntry[]>> {
    if (!directory.path) {
      return err('not_supported', 'Cannot scan directory without path')
    }

    const { extensions, onProgress, onError, signal, skipStats } = options
    const extensionSet = extensions?.length
      ? new Set(extensions.map((e) => e.toLowerCase().replace(/^\./, '')))
      : null

    try {
      const { fs } = await this.loadModules()
      const files: OneFSEntry[] = []
      const directoriesToScan: string[] = [directory.path]
      let totalScanned = 0

      while (directoriesToScan.length > 0) {
        if (signal?.aborted) {
          return err('cancelled', 'Scan was cancelled')
        }

        const currentDir = directoriesToScan.pop()!

        try {
          const dirEntries = await fs.readDir(currentDir)
          const fileEntriesToStat: { name: string; path: string }[] = []

          for (const entry of dirEntries) {
            if (!entry.name) continue

            if (!isSafeEntryName(entry.name)) continue
            const entryName = entry.name
            const entryPath = this.joinPath(currentDir, entryName)

            if (!isPathWithin(entryPath, directory.path)) continue

            if (entry.isDirectory) {
              directoriesToScan.push(entryPath)
            } else if (entry.isFile) {
              if (extensionSet) {
                const ext = entryName.split('.').pop()?.toLowerCase()
                if (!ext || !extensionSet.has(ext)) continue
              }

              if (skipStats) {
                files.push({ name: entryName, kind: 'file', path: entryPath })
              } else {
                fileEntriesToStat.push({ name: entryName, path: entryPath })
              }
            }

            totalScanned++
          }

          for (let i = 0; i < fileEntriesToStat.length; i += STAT_CHUNK_SIZE) {
            if (signal?.aborted) {
              return err('cancelled', 'Scan was cancelled')
            }

            const chunk = fileEntriesToStat.slice(i, i + STAT_CHUNK_SIZE)
            const stats = await Promise.all(
              chunk.map(entry =>
                fs.stat(entry.path).catch(statError => {
                  onError?.(entry.path, statError)
                  return null
                })
              )
            )
            for (let j = 0; j < chunk.length; j++) {
              const stat = stats[j]
              if (stat) {
                files.push({
                  name: chunk[j].name,
                  kind: 'file',
                  size: stat.size,
                  lastModified: stat.mtime ? new Date(stat.mtime).getTime() : Date.now(),
                  path: chunk[j].path,
                })
              } else {
                files.push({ name: chunk[j].name, kind: 'file', path: chunk[j].path })
              }
            }

            if (onProgress) {
              onProgress(totalScanned, files.length)
            }
          }

          if (onProgress && totalScanned % 100 === 0) {
            onProgress(totalScanned, files.length)
          }
          if (totalScanned % 500 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0))
          }
        } catch (dirError) {
          onError?.(currentDir, dirError)
        }
      }

      if (onProgress) {
        onProgress(totalScanned, files.length)
      }

      return ok(files)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'Directory not found', e)
      }
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to scan directory', e)
      }
      return err('io_error', error.message || 'Failed to scan directory', e)
    }
  }

  async getRecentFiles(): Promise<StoredHandle[]> {
    const files = await this.storage.getStoredFiles()
    return files.map((f) => ({
      id: f.id,
      name: f.name,
      path: f.path,
      type: f.mimeType === DIRECTORY_MIME_TYPE ? 'directory' as const : 'file' as const,
      storedAt: f.storedAt,
    }))
  }

  async restoreFile(stored: StoredHandle): Promise<OneFSResult<OneFSFile>> {
    const file = await this.storage.getStoredFile(stored.id)
    if (!file) {
      return err('not_found', 'File not found in storage')
    }

    if (file.mimeType === DIRECTORY_MIME_TYPE) {
      return err('not_found', 'Stored record is a directory - use restoreDirectory()')
    }

    if (file.path) {
      try {
        const { fs } = await this.loadModules()
        const content = await fs.readFile(file.path)
        const stat = await fs.stat(file.path)

        const restored: OneFSFile = {
          id: file.id,
          name: file.name,
          path: file.path,
          content,
          mimeType: file.mimeType,
          size: content.byteLength,
          lastModified: stat.mtime ? new Date(stat.mtime).getTime() : file.lastModified,
        }
        this.registerSessionFile(restored)
        return ok(restored)
      } catch (e) {
        const message = e instanceof Error ? e.message : 'File no longer accessible'
        return err('not_found', message, e)
      }
    }

    const restoredFromCache: OneFSFile = {
      id: file.id,
      name: file.name,
      path: file.path,
      content: file.content,
      mimeType: file.mimeType,
      size: file.size,
      lastModified: file.lastModified,
    }
    this.registerSessionFile(restoredFromCache)
    return ok(restoredFromCache)
  }

  async restoreDirectory(stored: StoredHandle): Promise<OneFSResult<OneFSDirectory>> {
    const file = await this.storage.getStoredFile(stored.id)
    if (!file || file.mimeType !== DIRECTORY_MIME_TYPE) {
      return err('not_found', 'Directory not found in storage')
    }

    if (!file.path) {
      return err('not_found', 'Directory path not found')
    }

    try {
      const { fs } = await this.loadModules()
      const stat = await fs.stat(file.path)
      if (!stat.isDirectory) {
        return err('not_found', 'Path is not a directory')
      }

      return ok({
        id: file.id,
        name: file.name,
        path: file.path,
      })
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'Directory no longer exists at original location', e)
      }
      return err('io_error', error.message || 'Failed to restore directory', e)
    }
  }

  async deleteFile(file: OneFSFile): Promise<OneFSResult<boolean>> {
    const authorized = await this.resolveAuthorizedPath(file)
    if (!authorized.ok) return authorized
    const path = authorized.data

    try {
      const { fs } = await this.loadModules()
      await fs.remove(path)
      await this.storage.removeFile(file.id)
      this.sessionPaths.delete(file.id)
      return ok(true)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'File not found', e)
      }
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to delete file', e)
      }
      return err('io_error', error.message || 'Failed to delete file', e)
    }
  }

  async renameFile(file: OneFSFile, newName: string): Promise<OneFSResult<OneFSFile>> {
    if (!isSafeEntryName(newName)) {
      return err('io_error', 'Invalid file name')
    }
    const authorized = await this.resolveAuthorizedPath(file)
    if (!authorized.ok) return authorized
    const oldPath = authorized.data

    try {
      const { fs } = await this.loadModules()
      const parentPath = this.splitParentPath(oldPath)
      const newPath = parentPath
        ? `${parentPath.parent}${parentPath.separator}${newName}`
        : newName

      await fs.rename(oldPath, newPath)

      const updatedFile: OneFSFile = {
        ...file,
        name: newName,
        path: newPath,
        mimeType: getMimeType(newName),
      }

      // Update the stored record's metadata without rewriting cached content
      // (the in-memory file.content may be stale). Best-effort: the disk
      // rename already succeeded.
      await this.storage
        .updateFileMetadata(file.id, { name: newName, path: newPath, mimeType: updatedFile.mimeType })
        .catch(() => {})
      this.registerSessionFile(updatedFile)

      return ok(updatedFile)
    } catch (e) {
      const error = e as Error
      if (error.message?.includes('No such file') || error.message?.includes('not found')) {
        return err('not_found', 'File not found', e)
      }
      if (error.message?.includes('Permission denied')) {
        return err('permission_denied', 'Permission denied to rename file', e)
      }
      return err('io_error', error.message || 'Failed to rename file', e)
    }
  }

  async removeFromRecent(id: string): Promise<void> {
    await this.storage.removeFile(id)
    this.sessionPaths.delete(id)
  }

  async clearRecent(): Promise<void> {
    await this.storage.clearFiles()
    this.sessionPaths.clear()
  }

  dispose(): void {
    this.storage.dispose()
    this.sessionPaths.clear()
  }

  async getFileUrl(file: OneFSFile): Promise<string> {
    if (!file.path) {
      return URL.createObjectURL(new Blob([toArrayBuffer(file.content)], { type: file.mimeType }))
    }

    try {
      const { core } = await this.loadModules()
      return core.convertFileSrc(file.path)
    } catch {
      return URL.createObjectURL(new Blob([toArrayBuffer(file.content)], { type: file.mimeType }))
    }
  }

  async getEntryUrl(entry: OneFSEntry): Promise<string | null> {
    if (!entry.path || entry.kind !== 'file') {
      return null
    }

    try {
      const { core } = await this.loadModules()
      return core.convertFileSrc(entry.path)
    } catch {
      return null
    }
  }
}
